import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MaterialReactTable } from "material-react-table";
import { useSelector } from "react-redux";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FilterListIcon from "@mui/icons-material/FilterList";
import GroupsIcon from "@mui/icons-material/Groups";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

import { axiosInstance } from "../../../../../api/axios";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { EmptyState } from "../../../components/common/EmptyState";
import { ImageUploadBox } from "../../../components/common/ImageUploadBox";
import { MRT_Localization_KO } from "../../../constants/localiztion";
import { FONT_PROPS } from "../../../styles/colors";
import { resolveEmployeeId } from "../../../utils/employeeIdentity";

const DEMO_EMP_ID = "6";

const INSPECTION_STATUS = [
  { value: "normal", label: "특이없음", color: "success", icon: <CheckCircleIcon /> },
  { value: "action_required", label: "조치요청", color: "warning", icon: <ReportProblemIcon /> },
  { value: "action_done", label: "조치완료", color: "info", icon: <FactCheckIcon /> },
];

const SLA_STATUS = [
  { value: "tracking", label: "진행중", color: "warning" },
  { value: "overdue", label: "기한초과", color: "error" },
  { value: "done", label: "완료", color: "success" },
];

const getToday = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toLocalDateString = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return {
    start: toLocalDateString(start),
    end: toLocalDateString(end),
  };
};

const getDefaultDateTime = (date = getToday(), hour = "09:00") => `${date}T${hour}`;

const getInspectionStatus = (status) =>
  INSPECTION_STATUS.find((item) => item.value === status) || INSPECTION_STATUS[0];

const getSlaStatus = (status) =>
  SLA_STATUS.find((item) => item.value === status) || SLA_STATUS[0];

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${axiosInstance.defaults.baseURL || ""}${photoUrl}`;
};

const makeEmptyForm = (date = getToday(), inspectorEmpId = "") => ({
  location_id: "",
  inspector_emp_id: inspectorEmpId,
  inspected_at: getDefaultDateTime(date, "09:00"),
  content: "",
  status: "normal",
  action_title: "",
  assignee_emp_id: "",
  due_at: getDefaultDateTime(date, "18:00"),
  instruction: "",
  action_result: "",
  completed_at: "",
  sla_status: "tracking",
});

const ACTION_FORM_KEYS = new Set([
  "action_title",
  "assignee_emp_id",
  "due_at",
  "instruction",
  "action_result",
  "completed_at",
  "sla_status",
]);

const buildEditForm = (row, date = getToday(), inspectorEmpId = "") => ({
  location_id: row.location_id || "",
  inspector_emp_id: row.inspector_emp_id ? String(row.inspector_emp_id) : inspectorEmpId,
  inspected_at: row.inspected_at_input || getDefaultDateTime(date, "09:00"),
  content: row.content || "",
  status: row.status || "normal",
  action_title: row.action_title || "",
  assignee_emp_id: row.assignee_emp_id ? String(row.assignee_emp_id) : "",
  due_at: row.due_at_input || getDefaultDateTime(date, "18:00"),
  instruction: row.instruction || "",
  action_result: row.action_result || "",
  completed_at: row.completed_at_input || "",
  sla_status: row.effective_sla_status || row.sla_status || "tracking",
});

const buildFormData = (form, photoFile, options = {}) => {
  const formData = new FormData();
  Object.entries(form).forEach(([key, value]) => {
    if (options.omitActionFields && ACTION_FORM_KEYS.has(key)) return;
    formData.append(key, value ?? "");
  });
  if (photoFile) {
    formData.append("photo", photoFile);
  }
  return formData;
};

const INSPECTION_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="inspection-header"]',
    title: "화면 흐름",
    description:
      "기간과 상태를 기준으로 안전점검 일지를 조회하고, 점검 결과와 조치 진행 상태를 한 화면에서 확인합니다.",
  },
  {
    target: '[data-autopilot="inspection-filters"]',
    title: "조회 조건",
    description:
      "시작일, 종료일, 점검 상태를 바꾸면 조건에 맞는 일지만 다시 조회됩니다.",
  },
  {
    target: '[data-autopilot="inspection-create"]',
    title: "점검 등록",
    description:
      "모바일에서는 현장 작업자가 점검 결과와 사진을 남기고, 웹에서는 관리자가 조치 담당자와 기한을 배정합니다.",
  },
  {
    target: '[data-autopilot="inspection-summary"]',
    title: "요약 지표",
    description:
      "전체 점검 건수, 조치요청, 기한초과, 조치완료율을 빠르게 확인합니다. 오늘 처리해야 할 건을 먼저 파악하기 좋습니다.",
  },
  {
    target: '[data-autopilot="inspection-list"]',
    title: "일지 목록",
    description:
      "웹에서는 테이블로 검색, 선택 삭제, 수정이 가능하고 모바일에서는 카드 목록으로 핵심 정보와 수정 진입을 제공합니다.",
  },
  {
    target: '[data-autopilot="inspection-table-actions"]',
    title: "검색과 삭제",
    description:
      "웹 테이블에서는 점검자, 점검 내용, 조치 제목으로 검색하고 선택한 일지를 일괄 삭제할 수 있습니다. 모바일은 목록 조회와 수정에 집중합니다.",
  },
];

function AutoPilotGuide({ open, steps, stepIndex, onStepChange, onClose }) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [targetRect, setTargetRect] = useState(null);
  const step = steps[stepIndex];

  useEffect(() => {
    if (!open || !step) return undefined;

    let rafId = null;
    const updateTargetRect = () => {
      const target = document.querySelector(step.target);
      if (!target) {
        setTargetRect(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      });
    };

    const target = document.querySelector(step.target);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }

    const scheduleUpdate = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateTargetRect);
    };

    const timeoutId = window.setTimeout(updateTargetRect, 120);
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);

    return () => {
      window.clearTimeout(timeoutId);
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate, true);
    };
  }, [open, step]);

  if (!open || !step) return null;

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const panelWidth = 380;
  const panelSx =
    isSmall || !targetRect
      ? {
          left: 16,
          right: 16,
          bottom: 16,
        }
      : {
          width: panelWidth,
          left:
            targetRect.left + targetRect.width / 2 > viewportWidth / 2
              ? Math.max(16, targetRect.left - panelWidth - 18)
              : Math.min(viewportWidth - panelWidth - 16, targetRect.right + 18),
          top: Math.min(Math.max(16, targetRect.top), viewportHeight - 240),
        };

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 4,
          bgcolor: "rgba(15, 23, 42, 0.42)",
          pointerEvents: "none",
        }}
      />
      {targetRect ? (
        <Box
          sx={{
            position: "fixed",
            zIndex: (muiTheme) => muiTheme.zIndex.modal + 5,
            top: Math.max(8, targetRect.top - 8),
            left: Math.max(8, targetRect.left - 8),
            width: Math.min(viewportWidth - 16, targetRect.width + 16),
            height: Math.min(viewportHeight - 16, targetRect.height + 16),
            border: "2px solid #2563eb",
            borderRadius: 2,
            boxShadow: "0 0 0 4px rgba(37, 99, 235, 0.18), 0 18px 48px rgba(15, 23, 42, 0.22)",
            pointerEvents: "none",
          }}
        />
      ) : null}
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 6,
          ...panelSx,
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          pointerEvents: "auto",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Chip
              size="small"
              color="primary"
              icon={<PlayCircleOutlineIcon />}
              label={`오토파일럿 ${stepIndex + 1}/${steps.length}`}
            />
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              {step.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.65 }}>
              {step.description}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={((stepIndex + 1) / steps.length) * 100}
            sx={{ height: 6, borderRadius: 999 }}
          />
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button
              color="inherit"
              startIcon={<NavigateBeforeIcon />}
              disabled={isFirst}
              onClick={() => onStepChange(stepIndex - 1)}
            >
              이전
            </Button>
            <Button
              variant="contained"
              endIcon={isLast ? <CheckCircleIcon /> : <NavigateNextIcon />}
              onClick={() => {
                if (isLast) {
                  onClose();
                  return;
                }
                onStepChange(stepIndex + 1);
              }}
            >
              {isLast ? "완료" : "다음"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}

function SummaryCard({ icon, label, value, color = "#2563eb", helper }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: `${color}1a`, color }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function MobileSummaryBar({ summary }) {
  const items = [
    { label: "전체", value: `${summary.total}건`, color: "#2563eb", icon: <FactCheckIcon fontSize="small" /> },
    { label: "조치요청", value: `${summary.required}건`, color: "#f59e0b", icon: <ReportProblemIcon fontSize="small" /> },
    { label: "기한초과", value: `${summary.overdue}건`, color: "#dc2626", icon: <AssignmentLateIcon fontSize="small" /> },
    { label: "완료율", value: `${summary.doneRate}%`, color: "#16a34a", icon: <CheckCircleIcon fontSize="small" /> },
  ];

  return (
    <Paper
      data-autopilot="inspection-summary"
      elevation={0}
      sx={{
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Grid container spacing={0.75}>
        {items.map((item) => (
          <Grid item xs={3} key={item.label}>
            <Stack
              spacing={0.4}
              alignItems="center"
              justifyContent="center"
              sx={{
                minHeight: 58,
                px: 0.5,
                borderRadius: 1.5,
                bgcolor: `${item.color}0f`,
                color: item.color,
                textAlign: "center",
              }}
            >
              {item.icon}
              <Typography variant="caption" sx={{ fontWeight: 800, color: "#475569", lineHeight: 1 }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1 }}>
                {item.value}
              </Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

function InspectionFormDialog({
  open,
  mode,
  form,
  employees,
  photoFile,
  isMobile,
  isSaving,
  isLoadingDetail,
  inspectorMissing,
  contentInputRef,
  isListening,
  speechSupport,
  onClose,
  onChange,
  onPhotoChange,
  onVoiceInput,
  onSubmit,
}) {
  const isActionNeeded = form.status !== "normal";
  const isEdit = mode === "edit";
  const statusLabel = isMobile ? "점검 결과" : "점검 상태";
  const statusOptions =
    isMobile && form.status !== "action_done"
      ? INSPECTION_STATUS.filter((status) => status.value !== "action_done")
      : INSPECTION_STATUS;
  const dialogTitle = isEdit
    ? isMobile
      ? "현장 안전점검 수정"
      : "안전점검 일지 수정"
    : isMobile
      ? "현장 안전점검 기록"
      : "안전점검 일지 등록";
  const submitLabel = isEdit ? "수정 완료" : isMobile ? "기록 저장" : "등록 완료";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: "calc(env(safe-area-inset-top, 0px) + 18px)", sm: 2 },
          pb: { xs: 1.5, sm: 1 },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {dialogTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isMobile
                ? "현장에서 발견한 내용을 사진과 함께 빠르게 남깁니다."
                : "작업자가 접수한 점검과 관리자 조치 배정을 관리합니다."}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          bgcolor: "#f7f8fb",
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 2 },
        }}
      >
        {inspectorMissing && !isLoadingDetail ? (
          <Alert severity={isMobile ? "warning" : "info"} sx={{ mb: 2, borderRadius: 2 }}>
            {isMobile
              ? "로그인 계정에 직원 정보가 연결되지 않아 점검 기록을 저장할 수 없습니다. 계정의 직원 정보를 연결한 뒤 다시 시도해주세요."
              : "로그인 계정에서 점검자를 자동으로 찾지 못했습니다. 아래에서 실제 점검자를 선택하면 저장할 수 있습니다."}
          </Alert>
        ) : null}

        {isLoadingDetail ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Grid container spacing={2.25}>
            <Grid item xs={12} md={isMobile ? 12 : 6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "#fff",
                }}
              >
                <Typography sx={{ fontWeight: 900, mb: 2 }}>점검 정보</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      type="datetime-local"
                      label="점검 일시"
                      value={form.inspected_at}
                      onChange={(event) => onChange("inspected_at", event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {!isMobile && inspectorMissing ? (
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>점검자 선택</InputLabel>
                        <Select
                          label="점검자 선택"
                          value={form.inspector_emp_id || ""}
                          onChange={(event) => onChange("inspector_emp_id", event.target.value)}
                        >
                          {employees.map((employee) => (
                            <MenuItem key={employee.emp_id} value={String(employee.emp_id)}>
                              {employee.emp_name || employee.emp_id}
                              {employee.department_name ? ` / ${employee.department_name}` : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.75, ml: 1.5 }}
                      >
                        저장 시 선택한 직원이 점검자로 기록됩니다.
                      </Typography>
                    </Grid>
                  ) : null}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{statusLabel}</InputLabel>
                      <Select
                        label={statusLabel}
                        value={form.status}
                        onChange={(event) => onChange("status", event.target.value)}
                      >
                        {statusOptions.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {isMobile && isActionNeeded ? (
                    <Grid item xs={12}>
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        조치요청으로 접수하면 관리자가 확인 후 담당자와 조치기한을 배정합니다.
                      </Alert>
                    </Grid>
                  ) : null}
                  <Grid item xs={12}>
                    <TextField
                      inputRef={contentInputRef}
                      fullWidth
                      multiline
                      minRows={5}
                      label="점검 내용"
                      placeholder="예: 프레스 2호기 주변 바닥 오일 누유 발견"
                      value={form.content}
                      onChange={(event) => onChange("content", event.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                            <Tooltip title={speechSupport.title}>
                              <IconButton
                                edge="end"
                                color={isListening ? "primary" : "default"}
                                onClick={onVoiceInput}
                                aria-label="점검 내용 음성 입력"
                                sx={{
                                  border: "1px solid",
                                  borderColor: isListening ? "primary.main" : "divider",
                                  borderRadius: 2,
                                  bgcolor: isListening ? "primary.lightOpacity" : "#fff",
                                }}
                              >
                                <KeyboardVoiceIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, ml: 1.5 }}>
                      {speechSupport.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <ImageUploadBox
                      label="현장 사진"
                      file={photoFile}
                      onFileChange={onPhotoChange}
                      capture="environment"
                      compact={isMobile}
                      helperText={
                        isMobile
                          ? "사진은 선택 사항이며, 큰 이미지는 자동으로 줄어듭니다."
                          : undefined
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {!isMobile ? (
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: isActionNeeded ? "warning.main" : "divider",
                  bgcolor: "#fff",
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <AssignmentLateIcon color={isActionNeeded ? "warning" : "disabled"} />
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>조치 관리</Typography>
                    <Typography variant="body2" color="text.secondary">
                      조치요청 또는 조치완료 상태일 때 조치 정보가 저장됩니다.
                    </Typography>
                  </Box>
                </Stack>

                {!isActionNeeded ? (
                  <Alert severity="success">특이없음 상태에서는 조치 항목이 생성되지 않습니다.</Alert>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="조치 제목"
                        placeholder="예: 조립 A라인 오일 누유 건"
                        value={form.action_title}
                        onChange={(event) => onChange("action_title", event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>담당자</InputLabel>
                        <Select
                          label="담당자"
                          value={form.assignee_emp_id || ""}
                          onChange={(event) => onChange("assignee_emp_id", event.target.value)}
                        >
                          {employees.map((employee) => (
                            <MenuItem key={employee.emp_id} value={String(employee.emp_id)}>
                              {employee.emp_name || employee.emp_id}
                              {employee.department_name ? ` / ${employee.department_name}` : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>SLA 상태</InputLabel>
                        <Select
                          label="SLA 상태"
                          value={form.sla_status}
                          onChange={(event) => onChange("sla_status", event.target.value)}
                        >
                          {SLA_STATUS.map((status) => (
                            <MenuItem key={status.value} value={status.value}>
                              {status.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        type="datetime-local"
                        label="조치 기한"
                        value={form.due_at}
                        onChange={(event) => onChange("due_at", event.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        label="완료 일시"
                        value={form.completed_at}
                        onChange={(event) => onChange("completed_at", event.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="조치 지시"
                        placeholder="예: 누유 원인 파악 및 밸브 교체"
                        value={form.instruction}
                        onChange={(event) => onChange("instruction", event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="조치 완료 내용"
                        placeholder="예: 밸브 교체 완료, 월 1회 점검 추가"
                        value={form.action_result}
                        onChange={(event) => onChange("action_result", event.target.value)}
                      />
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Grid>
            ) : null}
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 2 },
          gap: 1,
          justifyContent: { xs: "center", sm: "flex-end" },
          borderTop: { xs: "1px solid", sm: "none" },
          borderColor: "divider",
          bgcolor: "#fff",
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 44, sm: 36 } }}
        >
          취소
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={isSaving || isLoadingDetail}
          startIcon={isSaving ? null : <FactCheckIcon />}
          sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 44, sm: 36 } }}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MobileInspectionCard({ row, onEdit }) {
  const status = getInspectionStatus(row.status);
  const sla = row.action_id ? getSlaStatus(row.effective_sla_status || row.sla_status) : null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.25}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900 }}>
                {row.inspected_day} {row.inspected_time}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {row.location_path || row.location_name || "현장 점검"}
              </Typography>
            </Box>
            <Chip size="small" color={status.color} label={status.label} />
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {row.content || "점검 내용 없음"}
          </Typography>

          {row.action_id ? (
            <Paper variant="outlined" sx={{ p: 1.25, bgcolor: "#fff8e1" }}>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                    {row.action_title}
                  </Typography>
                  <Chip size="small" color={sla.color} label={sla.label} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  담당 {row.assignee_name || "-"} · 기한 {row.due_day || "-"} {row.due_time || ""}
                </Typography>
              </Stack>
            </Paper>
          ) : null}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" icon={<GroupsIcon />} label={row.inspector_department || "부서 미등록"} />
            {row.photo_url ? (
              <Chip
                size="small"
                icon={<CameraAltIcon />}
                label="사진"
                component="a"
                href={resolvePhotoUrl(row.photo_url)}
                target="_blank"
                clickable
                onClick={(event) => event.stopPropagation()}
              />
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(row)}>
          수정
        </Button>
      </CardActions>
    </Card>
  );
}

export function SafetyInspectionManagement() {
  const { group_code, project_code } = useParams();
  const [searchParams] = useSearchParams();
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const user = useSelector((state) => state.auth.user);
  const queryEmpId = searchParams.get("emp_id") || "";

  const defaultRange = useMemo(() => getMonthRange(), []);
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [statusFilter, setStatusFilter] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(makeEmptyForm());
  const [photoFile, setPhotoFile] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isContentListening, setIsContentListening] = useState(false);
  const contentInputRef = useRef(null);

  const speechSupport = useMemo(() => {
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const isAppleMobile = /iPad|iPhone|iPod/i.test(userAgent);
    const supportsBrowserSpeech =
      typeof window !== "undefined" && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

    if (isAppleMobile && !supportsBrowserSpeech) {
      return {
        supported: false,
        title: "iPhone/iPad 음성 입력",
        description: "입력칸을 선택한 뒤 키보드 하단의 마이크 버튼으로 점검 내용을 입력할 수 있습니다.",
      };
    }

    if (!supportsBrowserSpeech) {
      return {
        supported: false,
        title: "기기 키보드 음성 입력",
        description: "브라우저 직접 음성인식은 지원되지 않습니다. 입력칸을 선택한 뒤 키보드 마이크를 사용해주세요.",
      };
    }

    return {
      supported: true,
      title: "브라우저 음성 입력",
      description: "마이크 버튼을 누른 뒤 점검 내용을 말하면 입력칸에 자동으로 추가됩니다.",
    };
  }, []);

  const optionsQuery = useQuery({
    queryKey: ["risk-inspection-options", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/inspection/options");
      return response.data || { employees: [] };
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { employees: [] },
  });

  const inspectionsQuery = useQuery({
    queryKey: ["risk-inspection-logs", group_code, project_code, startDate, endDate, statusFilter],
    queryFn: async () => {
      const response = await api.get("/inspection/logs", {
        params: {
          start: startDate,
          end: endDate,
          status: statusFilter === "all" ? undefined : statusFilter,
        },
      });
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code && startDate && endDate),
    placeholderData: [],
  });

  const employees = useMemo(() => optionsQuery.data?.employees || [], [optionsQuery.data]);
  const resolvedInspectorEmpId = useMemo(
    () => resolveEmployeeId({ user, queryEmpId, employees }) || DEMO_EMP_ID,
    [employees, queryEmpId, user]
  );

  useEffect(() => {
    if (!resolvedInspectorEmpId) return;
    setForm((prev) =>
      prev.inspector_emp_id ? prev : { ...prev, inspector_emp_id: resolvedInspectorEmpId }
    );
  }, [resolvedInspectorEmpId]);

  const invalidateLogs = () => {
    queryClient.invalidateQueries({
      queryKey: ["risk-inspection-logs", group_code, project_code, startDate, endDate, statusFilter],
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildFormData(form, photoFile, { omitActionFields: isMobile });
      if (dialogMode === "edit") {
        return api.put(`/inspection/logs/${editingId}`, payload);
      }
      return api.post("/inspection/logs", payload);
    },
    onSuccess: () => {
      toast.success(dialogMode === "edit" ? "안전점검 일지가 저장되었습니다." : "안전점검 일지가 등록되었습니다.");
      closeDialog();
      invalidateLogs();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "안전점검 일지 저장 중 오류가 발생했습니다.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids) => api.delete("/inspection/logs", { data: { ids } }),
    onSuccess: () => {
      toast.success("선택한 안전점검 일지가 삭제되었습니다.");
      setRowSelection({});
      invalidateLogs();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "안전점검 일지 삭제 중 오류가 발생했습니다.");
    },
  });

  const rows = useMemo(() => inspectionsQuery.data || [], [inspectionsQuery.data]);

  const summary = useMemo(() => {
    const normal = rows.filter((row) => row.status === "normal").length;
    const required = rows.filter((row) => row.status === "action_required").length;
    const done = rows.filter((row) => row.status === "action_done").length;
    const overdue = rows.filter(
      (row) => (row.effective_sla_status || row.sla_status) === "overdue"
    ).length;
    const actionTotal = rows.filter((row) => row.action_id).length;
    const doneRate = actionTotal ? Math.round((done / actionTotal) * 100) : 0;
    return { total: rows.length, normal, required, done, overdue, actionTotal, doneRate };
  }, [rows]);

  const selectedIds = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((key) => Number(key))
        .filter(Boolean),
    [rowSelection]
  );

  const autopilotSteps = useMemo(
    () =>
      isMobile
        ? INSPECTION_AUTOPILOT_STEPS.filter(
            (step) => step.target !== '[data-autopilot="inspection-table-actions"]'
          )
        : INSPECTION_AUTOPILOT_STEPS,
    [isMobile]
  );

  useEffect(() => {
    if (autopilotStepIndex >= autopilotSteps.length) {
      setAutopilotStepIndex(Math.max(0, autopilotSteps.length - 1));
    }
  }, [autopilotStepIndex, autopilotSteps.length]);

  const openAutopilot = () => {
    if (isMobile) setMobileFiltersOpen(true);
    setAutopilotStepIndex(0);
    setAutopilotOpen(true);
  };

  const openCreateDialog = () => {
    setDialogMode("create");
    setEditingId(null);
    setForm(makeEmptyForm(getToday(), resolvedInspectorEmpId));
    setPhotoFile(null);
    setDialogOpen(true);
  };

  const openEditDialog = async (row) => {
    setDialogMode("edit");
    setEditingId(row.id);
    setDialogOpen(true);
    setPhotoFile(null);
    setIsLoadingDetail(true);

    try {
      const response = await api.get(`/inspection/logs/${row.id}`);
      setForm(buildEditForm(response.data || row, row.inspected_day, resolvedInspectorEmpId));
    } catch (error) {
      toast.error(error?.response?.data?.message || "안전점검 상세 조회 중 오류가 발생했습니다.");
      setForm(buildEditForm(row, row.inspected_day, resolvedInspectorEmpId));
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogMode("create");
    setEditingId(null);
    setPhotoFile(null);
    setForm(makeEmptyForm(getToday(), resolvedInspectorEmpId));
    setIsLoadingDetail(false);
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => {
      if (key === "status" && value === "action_done" && prev.sla_status !== "done") {
        return { ...prev, [key]: value, sla_status: "done" };
      }
      if (key === "completed_at" && value) {
        return { ...prev, [key]: value, sla_status: "done", status: "action_done" };
      }
      return { ...prev, [key]: value };
    });
  };

  const appendContentText = (text) => {
    handleFormChange("content", form.content ? `${form.content}\n${text}` : text);
  };

  const startContentVoiceInput = () => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      contentInputRef.current?.focus();
      toast.info(speechSupport.description);
      return;
    }

    const recognition = new Recognition();
    recognition.lang = "ko-KR";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    setIsContentListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || "";
      if (transcript) appendContentText(transcript);
    };
    recognition.onerror = () => {
      toast.warning("음성을 인식하지 못했습니다. 다시 시도해주세요.");
    };
    recognition.onend = () => {
      setIsContentListening(false);
    };
    recognition.start();
  };

  const handleSubmit = () => {
    if (!form.inspector_emp_id) {
      toast.warning(isMobile ? "점검자 정보가 연결되지 않았습니다." : "점검자를 선택해주세요.");
      return;
    }
    if (!form.inspected_at) {
      toast.warning("점검 일시를 입력해주세요.");
      return;
    }
    if (!isMobile && form.status !== "normal") {
      if (!String(form.action_title || "").trim()) {
        toast.warning("조치 제목을 입력해주세요.");
        return;
      }
      if (!form.assignee_emp_id) {
        toast.warning("조치 담당자를 선택해주세요.");
        return;
      }
      if (!form.due_at) {
        toast.warning("조치 기한을 입력해주세요.");
        return;
      }
    }
    saveMutation.mutate();
  };

  const handleDelete = () => {
    if (!selectedIds.length) {
      toast.warning("삭제할 안전점검 일지를 선택해주세요.");
      return;
    }
    if (window.confirm(`${selectedIds.length}건의 안전점검 일지를 삭제할까요?`)) {
      deleteMutation.mutate(selectedIds);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "inspected_at_input",
        header: "점검 일시",
        size: 150,
        Cell: ({ row }) => (
          <Stack spacing={0.3}>
            <Typography sx={{ fontWeight: 900 }}>{row.original.inspected_day}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.inspected_time}
            </Typography>
          </Stack>
        ),
      },
      {
        accessorKey: "inspector_name",
        header: "점검자",
        size: 150,
        Cell: ({ row }) => (
          <Stack spacing={0.3}>
            <Typography sx={{ fontWeight: 800 }}>{row.original.inspector_name || "-"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.inspector_department || "부서 미등록"}
            </Typography>
          </Stack>
        ),
      },
      {
        accessorKey: "content",
        header: "점검 내용",
        size: 280,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            title={cell.getValue() || ""}
            sx={{
              maxWidth: 320,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {cell.getValue() || "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "상태",
        size: 120,
        Cell: ({ cell }) => {
          const status = getInspectionStatus(cell.getValue());
          return <Chip size="small" color={status.color} label={status.label} />;
        },
      },
      {
        accessorKey: "action_title",
        header: "조치",
        size: 260,
        Cell: ({ row }) => {
          if (!row.original.action_id) return <Typography color="text.secondary">-</Typography>;
          const sla = getSlaStatus(row.original.effective_sla_status || row.original.sla_status);
          return (
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                  {row.original.action_title}
                </Typography>
                <Chip size="small" color={sla.color} label={sla.label} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                담당 {row.original.assignee_name || "-"} · 기한 {row.original.due_day || "-"} {row.original.due_time || ""}
              </Typography>
            </Stack>
          );
        },
      },
      {
        accessorKey: "photo_url",
        header: "사진",
        size: 100,
        Cell: ({ cell }) => {
          const photoUrl = cell.getValue();
          if (!photoUrl) return <Typography color="text.secondary">-</Typography>;
          return (
            <Button
              size="small"
              href={resolvePhotoUrl(photoUrl)}
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNewIcon />}
            >
              보기
            </Button>
          );
        },
      },
    ],
    []
  );

  const filterControls = (
    <>
      <TextField
        size="small"
        type="date"
        label="시작일"
        value={startDate}
        onChange={(event) => setStartDate(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        size="small"
        type="date"
        label="종료일"
        value={endDate}
        onChange={(event) => setEndDate(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel>상태</InputLabel>
        <Select
          label="상태"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setRowSelection({});
          }}
        >
          <MenuItem value="all">전체</MenuItem>
          {INSPECTION_STATUS.map((status) => (
            <MenuItem key={status.value} value={status.value}>
              {status.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 3 },
        height: "100%",
        overflow: isMobile ? "auto" : "hidden",
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack spacing={2.5} sx={{ flex: 1, minHeight: 0 }}>
        <Paper
          data-autopilot="inspection-header"
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          {isMobile ? (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <FactCheckIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 900 }} noWrap>
                    안전점검 관리
                  </Typography>
                </Stack>
                <Tooltip title="오토파일럿">
                  <IconButton
                    color="primary"
                    onClick={openAutopilot}
                    sx={{
                      width: 38,
                      height: 38,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      bgcolor: "#fff",
                    }}
                  >
                    <PlayCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Button
                data-autopilot="inspection-create"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                fullWidth
                sx={{ minHeight: 42, borderRadius: 2, fontWeight: 900 }}
              >
                현장 점검 기록
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon />}
                onClick={() => setMobileFiltersOpen((prev) => !prev)}
                sx={{
                  minHeight: 38,
                  borderRadius: 2,
                  px: 1.5,
                  fontWeight: 800,
                  color: "#334155",
                  borderColor: "divider",
                  bgcolor: mobileFiltersOpen ? "#f8fafc" : "#fff",
                }}
              >
                {mobileFiltersOpen ? "조회 조건 접기" : "조회 조건 보기"}
              </Button>
              <Collapse in={mobileFiltersOpen} timeout="auto" unmountOnExit>
                <Stack data-autopilot="inspection-filters" spacing={1} sx={{ pt: 0.5 }}>
                  {filterControls}
                </Stack>
              </Collapse>
            </Stack>
          ) : (
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FactCheckIcon color="primary" />
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>
                    안전점검 관리
                  </Typography>
                </Stack>
              </Box>

              <Stack data-autopilot="inspection-filters" direction={{ xs: "column", sm: "row" }} spacing={1}>
                {filterControls}
                <Tooltip title="오토파일럿">
                  <IconButton
                    color="primary"
                    onClick={openAutopilot}
                    sx={{
                      width: 40,
                      height: 40,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      bgcolor: "#fff",
                      "&:hover": {
                        bgcolor: "primary.lightOpacity",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <PlayCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  data-autopilot="inspection-create"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateDialog}
                >
                  등록
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>

        {isMobile ? (
          <MobileSummaryBar summary={summary} />
        ) : (
          <Grid data-autopilot="inspection-summary" container spacing={1.5}>
            <Grid item xs={6} md={2}>
              <SummaryCard icon={<FactCheckIcon />} label="전체" value={`${summary.total}건`} />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard icon={<CheckCircleIcon />} label="특이없음" value={`${summary.normal}건`} color="#16a34a" />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard icon={<ReportProblemIcon />} label="조치요청" value={`${summary.required}건`} color="#f59e0b" />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard icon={<AssignmentLateIcon />} label="기한초과" value={`${summary.overdue}건`} color="#dc2626" />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard icon={<FactCheckIcon />} label="조치완료" value={`${summary.done}건`} color="#0284c7" />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard
                icon={<GroupsIcon />}
                label="조치 완료율"
                value={`${summary.doneRate}%`}
                color="#16a34a"
              />
            </Grid>
          </Grid>
        )}

        {inspectionsQuery.isError ? (
          <Alert severity="error">안전점검 일지를 불러오는 중 오류가 발생했습니다.</Alert>
        ) : null}

        {isMobile ? (
          <Stack data-autopilot="inspection-list" spacing={1.25}>
            {inspectionsQuery.isLoading ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress />
              </Stack>
            ) : null}
            {rows.map((row) => (
              <MobileInspectionCard key={row.id} row={row} onEdit={openEditDialog} />
            ))}
            {!inspectionsQuery.isLoading && !rows.length ? (
              <EmptyState
                compact
                icon={<FactCheckIcon />}
                title="조회된 안전점검 일지가 없습니다"
                description="선택한 기간과 상태 조건에 맞는 점검 기록이 없습니다. 현장 점검 후 기록을 남기면 관리자가 조치 여부를 확인할 수 있습니다."
                actionLabel={isMobile ? "안전점검 기록" : "점검 일지 등록"}
                actionIcon={<AddIcon />}
                onAction={openCreateDialog}
              />
            ) : null}
          </Stack>
        ) : (
          <Paper
            data-autopilot="inspection-list"
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            <MaterialReactTable
              columns={columns}
              data={rows}
              localization={MRT_Localization_KO}
              getRowId={(row) => String(row.id)}
              enableRowSelection
              enableMultiRowSelection
              enableColumnActions={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnFilters={false}
              enableStickyHeader
              state={{
                isLoading: inspectionsQuery.isLoading,
                showProgressBars: inspectionsQuery.isFetching,
                globalFilter,
                rowSelection,
              }}
              onGlobalFilterChange={setGlobalFilter}
              onRowSelectionChange={setRowSelection}
              muiTablePaperProps={{
                sx: {
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "#fff",
                  boxShadow: "none",
                  "& *": {
                    fontFamily: `${FONT_PROPS.PRIMARY}, sans-serif !important`,
                  },
                },
              }}
              muiTableContainerProps={{
                sx: {
                  flex: 1,
                  minHeight: 0,
                  height: "100%",
                  maxHeight: "none",
                  bgcolor: "#fff",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  bgcolor: "#fff",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
              muiBottomToolbarProps={{
                sx: {
                  bgcolor: "#fff",
                  borderTop: "1px solid",
                  borderColor: "divider",
                },
              }}
              muiTableBodyProps={{
                sx: {
                  bgcolor: "#fff",
                },
              }}
              muiTableBodyRowProps={{
                sx: {
                  bgcolor: "#fff",
                  "&:hover td": {
                    bgcolor: "#fff",
                  },
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  bgcolor: "#fff",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  fontWeight: 900,
                  bgcolor: "#f1f5f9",
                },
              }}
              renderTopToolbarCustomActions={() => (
                <Stack
                  data-autopilot="inspection-table-actions"
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", md: "center" }}
                  sx={{ p: 1, width: "100%" }}
                >
                  <TextField
                    size="small"
                    placeholder="점검자, 내용, 조치 검색"
                    value={globalFilter || ""}
                    onChange={(event) => setGlobalFilter(event.target.value)}
                    sx={{ width: { xs: "100%", md: 320 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Divider orientation="vertical" flexItem />
                  {false ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      disabled={!selectedIds.length || deleteMutation.isPending}
                      onClick={handleDelete}
                    >
                      삭제
                    </Button>
                  ) : null}
                </Stack>
              )}
              enableRowActions
              positionActionsColumn="last"
              renderRowActions={({ row }) => (
                <Tooltip title="수정">
                  <IconButton onClick={() => openEditDialog(row.original)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              renderEmptyRowsFallback={() => (
                <EmptyState
                  compact
                  icon={<FactCheckIcon />}
                  title="조회된 안전점검 일지가 없습니다"
                  description="기간이나 상태 필터를 변경하거나 새 점검 일지를 등록해보세요."
                  actionLabel="점검 일지 등록"
                  actionIcon={<AddIcon />}
                  onAction={openCreateDialog}
                />
              )}
            />
          </Paper>
        )}
      </Stack>

      <AutoPilotGuide
        open={autopilotOpen}
        steps={autopilotSteps}
        stepIndex={autopilotStepIndex}
        onStepChange={setAutopilotStepIndex}
        onClose={() => setAutopilotOpen(false)}
      />

      <InspectionFormDialog
        open={dialogOpen}
        mode={dialogMode}
        form={form}
        employees={employees}
        photoFile={photoFile}
        isMobile={isMobile}
        isSaving={saveMutation.isPending}
        isLoadingDetail={isLoadingDetail}
        inspectorMissing={!form.inspector_emp_id}
        contentInputRef={contentInputRef}
        isListening={isContentListening}
        speechSupport={speechSupport}
        onClose={closeDialog}
        onChange={handleFormChange}
        onPhotoChange={setPhotoFile}
        onVoiceInput={startContentVoiceInput}
        onSubmit={handleSubmit}
      />
    </Box>
  );
}
